import { useQueries } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';

// Local types for dashboard report data shapes
interface StageDist { stage: string; _count: number; }
interface StatusDist { status: string; _count: number; }
interface MonthRev { paidAt: string; total: number; }

export function useDashboardStats() {
  const role = useAuthStore((s) => s.user?.role);
  const isClient = role === 'client';

  const results = useQueries({
    queries: [
      {
        queryKey: ['projects', { limit: '100' }],
        queryFn: () => apiClient.get('/projects', { limit: '100' }),
        staleTime: 60_000,
      },
      {
        queryKey: ['tasks', { limit: '100' }],
        queryFn: () => apiClient.get('/tasks', { limit: '100' }),
        staleTime: 60_000,
        enabled: !isClient,
      },
      {
        queryKey: ['report-time'],
        queryFn: () => apiClient.get('/reports/time'),
        staleTime: 60_000,
        enabled: !isClient,
      },
      {
        queryKey: ['report-financial'],
        queryFn: () => apiClient.get('/reports/financial'),
        staleTime: 60_000,
        enabled: !isClient,
      },
      {
        queryKey: ['report-projects'],
        queryFn: () => apiClient.get('/reports/projects'),
        staleTime: 60_000,
        enabled: !isClient,
      },
      {
        queryKey: ['invoice-stats'],
        queryFn: () => apiClient.get('/reports/invoice-stats'),
        staleTime: 60_000,
        enabled: !isClient,
      },
    ],
  });

  const [projectsQuery, tasksQuery, timeQuery, financialQuery, projectReportQuery, invoicesQuery] = results;
  const isLoading = results.some((r) => r.isLoading);

  const projects = projectsQuery?.data?.data || [];
  const tasks = tasksQuery?.data?.data || [];
  const totalHours = timeQuery?.data?.data?.totalHours ?? 0;
  const totalRevenue = financialQuery?.data?.data?.totalRevenue ?? 0;
  const projectReport = projectReportQuery?.data?.data;
  const financialReport = financialQuery?.data?.data;
  const invoiceStats = (invoicesQuery?.data?.data || {}) as { overdueInvoicesCount?: number; sentInvoicesCount?: number; totalOutstanding?: number };

  // Invoice stats (from lightweight /reports/invoice-stats endpoint)
  const overdueInvoicesCount = invoiceStats.overdueInvoicesCount ?? 0;
  const sentInvoicesCount = invoiceStats.sentInvoicesCount ?? 0;
  const totalOutstanding = invoiceStats.totalOutstanding ?? 0;

  const activeProjectsCount = projects.filter((p: any) => p.status !== 'completed').length;
  const pendingTasksCount = tasks.filter((t: any) => t.status !== 'done').length;

  // Recent projects
  const recentProjects = projects.slice(0, 5).map((p: any) => ({
    id: p.id,
    name: p.name,
    client: p.client?.companyName || p.client?.user?.name || 'Unknown',
    stage: p.stage,
    status: p.status,
    createdAt: p.createdAt,
  }));

  // Upcoming tasks
  const upcomingTasks = tasks
    .filter((t: any) => t.status !== 'done')
    .sort((a: any, b: any) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    })
    .slice(0, 5)
    .map((t: any) => ({
      id: t.id,
      title: t.title,
      project: t.project?.name || '',
      due: t.dueDate ? new Date(t.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'No due date',
      priority: t.priority,
    }));

  // Chart data
  const stageDistribution = (projectReport?.byStage as StageDist[])?.map((s: StageDist) => ({
    name: s.stage.charAt(0).toUpperCase() + s.stage.slice(1),
    count: s._count,
  })) || [];

  const statusDistribution = (projectReport?.byStatus as StatusDist[])?.map((s: StatusDist) => ({
    name: s.status.replace('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
    count: s._count,
  })) || [];

  const monthlyRevenue = (financialReport?.monthlyRevenue as MonthRev[])?.map((inv: MonthRev) => ({
    month: new Date(inv.paidAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
    revenue: inv.total,
  })) || [];

  return {
    data: {
      activeProjectsCount,
      pendingTasksCount,
      totalHours,
      totalRevenue,
      recentProjects,
      upcomingTasks,
      stageDistribution,
      statusDistribution,
      monthlyRevenue,
      overdueInvoicesCount,
      sentInvoicesCount,
      totalOutstanding,
    },
    isLoading,
  };
}
